import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Scale,
  Landmark,
  Coins,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Sparkles,
  BadgeCheck,
} from "lucide-react";
import { BRAND, IMAGES, telLinkPrimary, waLink } from "@/lib/brand";
import BrandStamp from "@/components/BrandStamp";
import usePageMeta from "@/hooks/usePageMeta";

const PhoneStrip = () => (
  <div className="bg-[#641C24] text-[#FFFDF8] border-y border-[#D4AF37]/40">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="inline-flex items-center justify-center w-9 h-9 bg-[#D4AF37] text-[#2D181A]">
          <Phone size={16} strokeWidth={2.2} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Talk to a Gold Expert · अभी विशेषज्ञ से बात करें
          </div>
          <a
            href={telLinkPrimary}
            data-testid="loanvssell-phone-strip"
            className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[#FFFDF8] hover:text-[#D4AF37] transition-colors"
          >
            {BRAND.phonePrimary}
          </a>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={waLink(
            "Namaste! Emergency fund ke liye main confused hoon — gold loan lena chahiye ya sona bech dena? Kripya guide karein. (Top Rate)"
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <MessageCircle size={15} /> WhatsApp
        </a>
        <a
          href={telLinkPrimary}
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <Phone size={15} /> Call Expert
        </a>
      </div>
    </div>
  </div>
);

export default function GoldLoanVsSellPage() {
  usePageMeta({
    title:
      "Gold Loan or Selling Gold — When You Need Emergency Funds | Top Rate Gurgaon Guide",
    description:
      "Should you take a gold loan or sell your gold in an emergency? A clear head-to-head comparison with cost math, a simple rule, and expert advice. Call " +
      BRAND.phonePrimary +
      " to speak to a Top Rate gold expert.",
    keywords:
      "gold loan vs selling gold, gold loan or cash for gold, emergency funds India, gold loan calculator, gold loan interest rate, sell gold or take loan, gold loan Gurgaon, pledged gold buyback, loan-to-value gold India, gold loan settlement",
    path: "/gold-loan-vs-selling-gold",
  });

  return (
    <div>
      <PhoneStrip />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FAF6EC]">
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-20 hidden md:block"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,253,248,1) 0%, rgba(255,253,248,0.95) 45%, rgba(255,253,248,0.35) 75%, rgba(255,253,248,0) 100%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 tr-fade-up">
              <div className="inline-flex items-center gap-2 border border-[#641C24]/40 bg-[#FFFDF8] text-[#641C24] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold">
                <Scale size={13} strokeWidth={2} />
                Emergency Fund Guide · आपातकालीन गाइड
              </div>

              <h1 className="font-display text-[#2D181A] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.0] mt-6">
                Gold Loan or Selling Gold —{" "}
                <span className="italic text-[#B8860B]">
                  When You Need Emergency Funds?
                </span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-[#4A3B3C] leading-relaxed max-w-2xl">
                Both options unlock instant liquidity from your gold — but
                only one is right for your situation. Here's the honest,
                numbers-first comparison from Top Rate experts. Confused?{" "}
                <span className="text-[#641C24] font-semibold">
                  Just call — we'll advise, not push.
                </span>
              </p>

              {/* PRIMARY PHONE - BIG */}
              <a
                href={telLinkPrimary}
                data-testid="loanvssell-hero-phone"
                className="mt-6 inline-flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-5 bg-[#641C24] text-[#FFFDF8] pl-5 pr-6 py-4 sm:py-5 shadow-[0_10px_30px_-10px_rgba(100,28,36,0.55)] hover:bg-[#4A151A] transition-colors w-full sm:w-auto"
              >
                <span className="inline-flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 bg-[#D4AF37] text-[#2D181A] shrink-0">
                    <Phone size={18} strokeWidth={2.4} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
                    Call Our Expert · मुफ्त सलाह
                  </span>
                </span>
                <span className="font-display font-black tracking-tight text-3xl sm:text-4xl md:text-5xl leading-none text-[#FFFDF8] tabular-nums">
                  {BRAND.phonePrimary}
                </span>
              </a>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div className="border-l-2 border-[#D4AF37] pl-3 text-[#2D181A]">
                  <div className="uppercase tracking-[0.16em] font-bold text-[#641C24]">
                    Gold Loan
                  </div>
                  <div className="mt-0.5">Keep ownership</div>
                </div>
                <div className="border-l-2 border-[#D4AF37] pl-3 text-[#2D181A]">
                  <div className="uppercase tracking-[0.16em] font-bold text-[#641C24]">
                    Cash for Gold
                  </div>
                  <div className="mt-0.5">Permanent liquidity</div>
                </div>
                <div className="border-l-2 border-[#D4AF37] pl-3 text-[#2D181A]">
                  <div className="uppercase tracking-[0.16em] font-bold text-[#641C24]">
                    Pledged Gold
                  </div>
                  <div className="mt-0.5">Bank buyback</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 flex items-center justify-center">
              <div className="relative w-full max-w-md">
                <img
                  src={IMAGES.ornament1}
                  alt="Gold jewellery evaluation Top Rate Gurgaon"
                  loading="eager"
                  className="w-full h-72 md:h-80 object-cover shadow-[0_18px_44px_-14px_rgba(45,24,26,0.4)]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt="₹500 notes emergency cash for gold"
                  loading="eager"
                  className="absolute -bottom-8 -right-4 w-3/5 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.5)] rotate-[6deg]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute -bottom-4 -left-3 w-2/5 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.4)] -rotate-[6deg] hidden sm:block"
                />
                <div className="absolute -top-5 -left-3 bg-[#641C24] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Compare Both ⚖
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={<>Loan · Cash for Gold · Bank Pledge Buyback — All Under One Roof</>}
      />

      {/* HEAD-TO-HEAD */}
      <section
        data-testid="loanvssell-comparison"
        className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24"
      >
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
          Head-to-Head · तुलना
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
          The right choice depends on{" "}
          <span className="italic text-[#641C24]">whether you'll need the gold again</span>.
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mt-12">
          {/* GOLD LOAN */}
          <div className="bg-[#FAF6EC] border border-[#E5DCC5] p-8 md:p-10 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#641C24] text-[#D4AF37] flex items-center justify-center">
                <Landmark size={22} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
                  Option 01
                </div>
                <div className="font-display text-2xl font-bold leading-tight">
                  Take a Gold Loan
                </div>
              </div>
            </div>
            <p className="text-sm text-[#4A3B3C] leading-relaxed mt-5">
              You pledge your gold as security. The lender gives you a loan
              typically at{" "}
              <span className="font-semibold text-[#2D181A]">
                75% of the gold's value (Loan-to-Value ratio)
              </span>
              . You pay interest, and once repaid — your gold comes back to
              you, exactly as it was.
            </p>

            <div className="mt-6 text-xs uppercase tracking-[0.22em] text-[#641C24] font-bold">
              Choose Gold Loan if:
            </div>
            <ul className="mt-3 space-y-2.5 text-sm text-[#2D181A]">
              {[
                "You need money only temporarily",
                "The jewellery has sentimental or family value",
                "You expect to repay within a reasonable period",
                "You believe gold price may rise — you want to keep ownership",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2.5">
                  <CheckCircle2
                    size={16}
                    className="mt-0.5 text-[#641C24] shrink-0"
                  />
                  {x}
                </li>
              ))}
            </ul>
          </div>

          {/* SELL GOLD */}
          <div className="bg-[#2D181A] text-[#FFFDF8] p-8 md:p-10 border-l-4 border-[#D4AF37]">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#D4AF37] text-[#2D181A] flex items-center justify-center">
                <Coins size={22} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                  Option 02
                </div>
                <div className="font-display text-2xl font-bold leading-tight text-[#FFFDF8]">
                  Sell Your Gold for Cash
                </div>
              </div>
            </div>
            <p className="text-sm text-[#FFFDF8]/85 leading-relaxed mt-5">
              You transfer ownership of the gold and get{" "}
              <span className="font-semibold text-[#FFFDF8]">
                100% of the market value
              </span>{" "}
              in cash or bank transfer — no interest, no monthly payments, no
              risk of default. The transaction is over the moment you walk
              out.
            </p>

            <div className="mt-6 text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              Choose Selling if:
            </div>
            <ul className="mt-3 space-y-2.5 text-sm text-[#FFFDF8]/90">
              {[
                "You don't need the jewellery anymore",
                "You need money permanently, not just for a few months",
                "You're unsure you'll be able to repay a loan",
                "The jewellery is old, broken, or has little sentimental value",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2.5">
                  <CheckCircle2
                    size={16}
                    className="mt-0.5 text-[#D4AF37] shrink-0"
                  />
                  {x}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#FFFDF8]/70 leading-relaxed mt-5 border-t border-[#D4AF37]/25 pt-4">
              Selling avoids paying interest and eliminates the risk of losing
              the jewellery through loan default.
            </p>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={<>Top Rate · Trusted Advice · Best Rate Whichever Path You Choose</>}
      />

      {/* COST COMPARISON EXAMPLE */}
      <section className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
            Cost Comparison · A Real Example
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Suppose your gold is worth{" "}
            <span className="italic text-[#641C24]">₹5,00,000</span>.
          </h2>
          <p className="text-[#4A3B3C] leading-relaxed mt-4 max-w-2xl">
            Here's what actually happens in each path — with real numbers.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mt-12">
            {/* Loan math */}
            <div className="bg-white border border-[#E5DCC5] p-8 md:p-10 shadow-sm">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
                <Landmark size={14} /> Gold Loan Math
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold mt-3 leading-tight">
                You receive a loan — but you also pay interest.
              </h3>
              <dl className="mt-6 divide-y divide-[#E5DCC5]">
                {[
                  ["Gold value", "₹5,00,000"],
                  ["Loan amount (~75% LTV)", "₹3,75,000"],
                  ["Interest rate (typical)", "~12% per year"],
                  ["Interest for 1 year", "₹45,000"],
                  ["Fees / processing", "Additional"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between py-3"
                  >
                    <dt className="text-sm text-[#4A3B3C]">{k}</dt>
                    <dd className="font-display font-bold text-[#2D181A] tabular-nums">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 bg-[#FAF6EC] border-l-4 border-[#641C24] px-4 py-3 text-xs text-[#2D181A]">
                <span className="font-semibold">Net:</span> Loan of ₹3,75,000
                — cost you roughly ₹45,000 + fees over one year. Gold is
                returned when the loan is fully repaid.
              </div>
            </div>

            {/* Sell math */}
            <div className="bg-[#641C24] text-[#FFFDF8] p-8 md:p-10 border-l-4 border-[#D4AF37]">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                <Coins size={14} /> Selling Math
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold mt-3 leading-tight text-[#FFFDF8]">
                You receive full market value — no interest, ever.
              </h3>
              <dl className="mt-6 divide-y divide-[#D4AF37]/25">
                {[
                  ["Gold value", "₹5,00,000"],
                  ["Sale proceeds (received)", "~₹5,00,000"],
                  ["Interest paid", "₹0"],
                  ["Fees / processing", "₹0"],
                  ["Ownership after sale", "Transferred"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between py-3"
                  >
                    <dt className="text-sm text-[#FFFDF8]/80">{k}</dt>
                    <dd className="font-display font-bold text-[#D4AF37] tabular-nums">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 bg-[#2D181A]/60 border-l-4 border-[#D4AF37] px-4 py-3 text-xs text-[#FFFDF8]/90">
                <span className="font-semibold">Net:</span> Sale proceeds
                immediately. No interest to pay. But you no longer own the
                gold.
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="light"
        tagline={<>Numbers Don't Lie · Emergency Funds · The Top Rate Way</>}
      />

      {/* SIMPLE RULE */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
          Simple Rule · सरल नियम
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
          The one-line rule to decide{" "}
          <span className="italic text-[#641C24]">right now</span>.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-12">
          <div className="bg-[#FAF6EC] border-l-4 border-[#641C24] p-8">
            <div className="text-4xl">🔁</div>
            <div className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight">
              Need the gold back?
            </div>
            <div className="mt-3 inline-flex items-center gap-2 text-[#641C24] font-semibold uppercase tracking-[0.2em] text-sm">
              → Take a Gold Loan
            </div>
          </div>
          <div className="bg-[#D4AF37] text-[#2D181A] p-8">
            <div className="text-4xl">💸</div>
            <div className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight">
              Don't want the gold anymore?
            </div>
            <div className="mt-3 inline-flex items-center gap-2 text-[#641C24] font-semibold uppercase tracking-[0.2em] text-sm">
              → Sell it (Cash for Gold)
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={<>One Rule · Two Paths · Top Rate Handles Both Beautifully</>}
      />

      {/* FINANCIAL DEEP-DIVE */}
      <section className="bg-[#2D181A] text-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                Which is Financially Better?
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] text-[#FFFDF8]">
                It's less about "better" — more about your{" "}
                <span className="italic text-[#D4AF37]">
                  confidence to repay
                </span>
                .
              </h2>
              <div className="mt-6 space-y-5 text-base text-[#FFFDF8]/85 leading-relaxed">
                <p>
                  <span className="text-[#D4AF37] font-semibold">
                    If you're confident you can repay the loan
                  </span>{" "}
                  within a relatively short period, a gold loan is often the
                  better financial choice — you retain ownership of an asset
                  that may appreciate over time.
                </p>
                <p>
                  <span className="text-[#D4AF37] font-semibold">
                    If repayment is uncertain, selling can be the safer option
                  </span>
                  . Defaulting on a gold loan means you still pay interest —
                  and may ultimately lose the jewellery anyway through
                  auction. Selling avoids that entirely.
                </p>
                <p>
                  In short, the decision is less about which option is
                  "better" in general and more about{" "}
                  <span className="text-[#FFFDF8] font-semibold">
                    whether you value keeping the gold
                  </span>{" "}
                  and{" "}
                  <span className="text-[#FFFDF8] font-semibold">
                    whether you're confident in your ability to repay
                  </span>
                  .
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-[#FFFDF8] text-[#2D181A] p-8 shadow-2xl">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
                  <Sparkles size={13} /> Confused? Just Call.
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight">
                  Speak to a Top Rate expert.
                </h3>
                <p className="text-sm text-[#4A3B3C] leading-relaxed mt-3">
                  A short call is all it takes. Share your gold weight,
                  purity and how long you need funds — our senior valuer will
                  give you an honest recommendation.
                </p>

                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex items-start gap-3">
                    <BadgeCheck
                      size={18}
                      className="mt-0.5 text-[#641C24] shrink-0"
                    />
                    <div>
                      <div className="font-semibold text-[#2D181A]">
                        Gold Loan
                      </div>
                      <div className="text-xs text-[#4A3B3C]">
                        Rate, LTV, tenure — quoted upfront.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BadgeCheck
                      size={18}
                      className="mt-0.5 text-[#641C24] shrink-0"
                    />
                    <div>
                      <div className="font-semibold text-[#2D181A]">
                        Cash for Gold
                      </div>
                      <div className="text-xs text-[#4A3B3C]">
                        Live karat testing, spot cash or bank transfer.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BadgeCheck
                      size={18}
                      className="mt-0.5 text-[#641C24] shrink-0"
                    />
                    <div>
                      <div className="font-semibold text-[#2D181A]">
                        Pledged Gold with Bank
                      </div>
                      <div className="text-xs text-[#4A3B3C]">
                        We settle directly with your lender.
                      </div>
                    </div>
                  </div>
                </div>

                <a
                  href={telLinkPrimary}
                  data-testid="loanvssell-expert-call"
                  className="mt-6 flex items-center justify-between gap-3 bg-[#641C24] hover:bg-[#4A151A] text-[#FFFDF8] px-6 py-4"
                >
                  <span className="inline-flex items-center gap-2">
                    <Phone size={16} strokeWidth={2.2} />
                    <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                      Call Expert
                    </span>
                  </span>
                  <span className="font-display font-black text-xl md:text-2xl tracking-tight">
                    {BRAND.phonePrimary}
                  </span>
                </a>
                <a
                  href={waLink(
                    "Namaste! Gold loan vs selling — advise karein based on mera scenario. (Top Rate)"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1FBA57] text-white px-6 py-4"
                >
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle size={16} strokeWidth={2.2} />
                    <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                      WhatsApp
                    </span>
                  </span>
                  <span className="font-display font-black text-xl md:text-2xl tracking-tight">
                    Chat Now
                  </span>
                </a>

                <div className="mt-6 flex flex-wrap gap-3 border-t border-[#E5DCC5] pt-4">
                  <Link
                    to="/sell-gold-gurgaon"
                    className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#641C24] hover:text-[#B8860B] inline-flex items-center gap-1"
                  >
                    Cash for Gold <ArrowRight size={12} />
                  </Link>
                  <Link
                    to="/mortgaged-gold"
                    className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#641C24] hover:text-[#B8860B] inline-flex items-center gap-1"
                  >
                    Pledged Gold Buyback <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={
          <>Top Rate · Sona Do Paisa Lo · Loan · Cash · Bank Pledge Buyback</>
        }
      />
    </div>
  );
}
